import { useEffect, useState } from 'react';
import { ExactLimitsBoard, getExactLimitsBoard } from '../../../api/exactLimitsBoard';
import { useAxiosSwagger } from '../../../hooks/useAxiosSwagger';
import { dayShown, LimitsView, minuteShown, Shown } from './exactLimitsModel';
import { formatDuration } from './runDetailModel';

// From when what is left is coloured; the same thresholds as the Exact panel on the environment page.
const WARN_BELOW = 0.25;
const BAD_BELOW = 0.1;

// How often the board is asked while a run goes: often enough that a minute running out shows within seconds.
const BOARD_POLL_MS = 2000;

const count = (value: number) => value.toLocaleString('en-US');

/** Ticks every second while the run goes, so the resets count down. */
const useNow = (ticking: boolean) => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!ticking) return;

        const timer = setInterval(() => setNow(Date.now()), 1000);

        return () => clearInterval(timer);
    }, [ticking]);

    return now;
};

/**
 * The environment's board of Exact's limits, asked every two seconds while {@link active}: what every caller with
 * the connector's token was last told, not only the run itself.
 */
export const useExactLimitsBoard = (customer: string, environment: string, active: boolean) => {
    const axios = useAxiosSwagger();
    const [board, setBoard] = useState<ExactLimitsBoard>();

    useEffect(() => {
        if (!axios || !active || !customer || !environment) return;

        let stopped = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const ask = async () => {
            try {
                const answer = await getExactLimitsBoard(axios, customer, environment);

                if (!stopped && !answer.error) setBoard(answer);
            } catch {
                // A missed read only leaves the last one on screen; the next one comes in two seconds.
            }

            if (!stopped) timer = setTimeout(ask, BOARD_POLL_MS);
        };

        ask();

        return () => {
            stopped = true;

            if (timer) clearTimeout(timer);
        };
    }, [axios, active, customer, environment]);

    return board;
};

const Meter = ({
    label,
    shortLabel,
    shown,
    now,
    compact,
    hint,
}: {
    label: string;
    shortLabel: string;
    shown: Shown;
    now: number;
    compact: boolean;
    hint: string;
}) => {
    const { remaining, limit, resetsAt, simulated } = shown;
    const left = limit ? Math.min(Math.max(remaining / limit, 0), 1) : undefined;
    const tone = left === undefined ? 'idle' : left <= BAD_BELOW ? 'bad' : left <= WARN_BELOW ? 'warn' : 'ok';
    const used = left === undefined ? 0 : Math.round((1 - left) * 100);
    // In local time: Exact counts its day in UTC, so in Amsterdam the day starts over at 01:00 or 02:00.
    const resetsAtClock = resetsAt !== undefined && resetsAt > now ? new Date(resetsAt).toLocaleTimeString() : undefined;

    return (
        <div
            className={`rd-limit rd-limit--${tone}${compact ? ' rd-limit--compact' : ''}`}
            title={resetsAtClock ? `${hint} Starts over at ${resetsAtClock}.` : hint}
        >
            <div className="rd-limit__head">
                <span className="rd-limit__label">
                    {compact ? shortLabel : label}
                    {simulated && ' (simulated)'}
                </span>
                <span className="rd-limit__numbers">{limit ? `${count(remaining)} / ${count(limit)}` : count(remaining)}</span>
            </div>
            <div
                className="rd-limit__track"
                role="progressbar"
                aria-label={`${label}: share used`}
                aria-valuenow={used}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <span className="rd-limit__fill" style={{ width: `${used}%` }} />
            </div>
            {!compact && resetsAt !== undefined && resetsAtClock && (
                <div className="rd-limit__reset">
                    resets at {resetsAtClock}, in {formatDuration(resetsAt - now)}
                </div>
            )}
        </div>
    );
};

/**
 * What is left of Exact's daily and minutely limits, as the newest answer to anyone using the environment's token
 * said: the connector, the run, the dashboard.
 *
 * The limits count per app per company, so the run and the connector it tests share them: a day that runs out stops
 * the connector too, and a full minute is why a wait suddenly takes longer.
 */
export const ExactLimits = ({
    limits,
    running,
    compact = false,
}: {
    limits?: LimitsView;
    running: boolean;
    compact?: boolean;
}) => {
    const now = useNow(running && !!limits);

    if (!limits) return null;

    const day = dayShown(limits, now);
    const minute = minuteShown(limits, now);

    return (
        <div className={`rd-limits${compact ? ' rd-limits--compact' : ''}`}>
            {compact && <span className="rd-limits__title">Exact</span>}
            {day && (
                <Meter
                    label="Exact today"
                    shortLabel="day"
                    shown={day}
                    now={now}
                    compact={compact}
                    hint={
                        day.simulated
                            ? 'The connector acts as if the day is used up, for a scenario; Exact itself still has calls.'
                            : "Calls left in Exact's 24-hour window, shared by the connector, the run and the dashboard. Once it is used up Exact refuses everything until the reset."
                    }
                />
            )}
            <Meter
                label="Exact this minute"
                shortLabel="minute"
                shown={minute}
                now={now}
                compact={compact}
                hint="Calls left in this minute, shared by the connector, the run and the dashboard. A full minute makes every call wait for the next one."
            />
        </div>
    );
};
